import React, { useState, useCallback, useRef, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import Topbar from "../../Components/Topbar/Topbar";
import { initCriteriaStates, NORMS, type CriterionState } from "./norms/normConfig";
import type { User } from "../../types/user";
import { Building2, LayoutDashboard, ClipboardCheck, FolderOpen, BookOpen, Target, ChevronDown } from "lucide-react";
import "./AlignmentModule.css";

export interface ContextFormData {
  companyName: string;
  sector: string;
  size: string;
  countries: string[];
  scope: string;
  mainActivities: string[];
  environmentalImpacts: string[];
  socialProfile: string[];
  criticalSuppliers: string[];
  rseFunctions: string[];
  rsePolicies: string[];
  certifications: string[];
  esgIndicators: string[];
  ethicsTopics: string[];
  docMaturity: string[];
  dataAvailability: string[];
  riskSites: string;
  esgPriorities: string;
  gri_reportingObjective: string;
  gri_reportingPeriod: string;
  gri_externalAssurance: string;
  gri_reportingBoundaries: string;
  gri_materialityProcess: string;
  gri_materialTopics: string[];
  gri_sectorStandards: string;
  gri_dataOwners: string;
}

export interface AlignmentOutletContext {
  selectedNorm: string;
  user: User;
  criteriaStates: Record<string, CriterionState>;
  updateCriterionState: (id: string, updates: Partial<CriterionState>) => void;
  contextData: ContextFormData;
  setContextData: React.Dispatch<React.SetStateAction<ContextFormData>>;
  setSelectedNorm: (norm: string) => void;
}

const emptyContext: ContextFormData = {
  companyName: "", sector: "", size: "",
  countries: [], scope: "",
  mainActivities: [], environmentalImpacts: [], socialProfile: [], criticalSuppliers: [],
  rseFunctions: [], rsePolicies: [], certifications: [], esgIndicators: [],
  ethicsTopics: [], docMaturity: [], dataAvailability: [],
  riskSites: "", esgPriorities: "",
  gri_reportingObjective: "", gri_reportingPeriod: "", gri_externalAssurance: "",
  gri_reportingBoundaries: "", gri_materialityProcess: "",
  gri_materialTopics: [], gri_sectorStandards: "", gri_dataOwners: "",
};

const tabs = [
  { to: "/alignement-referentiel/context",     label: "Contexte entreprise", Icon: Building2       },
  { to: "/alignement-referentiel/dashboard",   label: "Dashboard",           Icon: LayoutDashboard },
  { to: "/alignement-referentiel/evaluation",  label: "Évaluation",          Icon: ClipboardCheck  },
  { to: "/alignement-referentiel/dossier",     label: "Dossier d'audit",     Icon: FolderOpen      },
  { to: "/alignement-referentiel/referentiel", label: "Référentiel",         Icon: BookOpen        },
  { to: "/alignement-referentiel/objectif",    label: "Objectif",            Icon: Target          },
];

interface Props {
  user: User;
}

const AlignmentModule: React.FC<Props> = ({ user }) => {
  const navigate = useNavigate();
  const [selectedNorm, setSelectedNorm] = useState("GRI");
  const [criteriaStates, setCriteriaStates] = useState<Record<string, CriterionState>>(initCriteriaStates);
  const [contextData, setContextData] = useState<ContextFormData>(emptyContext);
  const [normDropOpen, setNormDropOpen] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);

  const currentNorm = NORMS.find((n) => n.key === selectedNorm) ?? NORMS[0];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropRef.current && !dropRef.current.contains(e.target as Node))
        setNormDropOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const updateCriterionState = useCallback((id: string, updates: Partial<CriterionState>) => {
    setCriteriaStates((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...updates },
    }));
  }, []);

  const outletContext: AlignmentOutletContext = {
    selectedNorm,
    setSelectedNorm,
    user,
    criteriaStates,
    updateCriterionState,
    contextData,
    setContextData,
  };

  return (
    <div className="am-wrapper">
      <Topbar
        title="Alignement Référentiel"
        userName={user.username}
        onLogout={() => navigate("/login")}
        user={user}
      />

      <div className="am-module-header">
        <div className="am-tabs-bar">
          <nav className="am-tabs">
            {tabs.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `am-tab${isActive ? " am-tab--active" : ""}`}
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="am-norm-selector" ref={dropRef}>
            <span className="am-norm-badge" style={{ backgroundColor: currentNorm.color + "22", color: currentNorm.color }}>
              {currentNorm.label}
            </span>
            <button
              className="am-norm-btn"
              onClick={() => setNormDropOpen((o) => !o)}
            >
              <img src={currentNorm.img} alt={currentNorm.label} className="am-norm-img" />
              <span className="am-norm-name">{currentNorm.fullName}</span>
              <ChevronDown
                size={14}
                className={`am-norm-chevron${normDropOpen ? " am-norm-chevron--open" : ""}`}
              />
            </button>
            {normDropOpen && (
              <div className="am-norm-dropdown">
                {NORMS.map((n) => (
                  <button
                    key={n.key}
                    className={`am-norm-option${n.key === selectedNorm ? " am-norm-option--active" : ""}`}
                    onClick={() => { setSelectedNorm(n.key); setNormDropOpen(false); }}
                  >
                    <img src={n.img} alt={n.label} className="am-norm-option-img" />
                    <div className="am-norm-option-text">
                      <span className="am-norm-option-label">{n.label}</span>
                      <span className="am-norm-option-full">{n.fullName}</span>
                    </div>
                    {!n.implemented && <span className="am-wip-badge">Bientôt</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <main className="am-content">
        <Outlet context={outletContext} />
      </main>
    </div>
  );
};

export default AlignmentModule;
